import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { brl, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, FileUp, PackagePlus, Receipt } from "lucide-react";

const MAX_BYTES = 2 * 1024 * 1024;
const ORIGEM = {
  VINCULO_ANTERIOR: "Sugerida: já vinculada em nota anterior",
  CODIGO_FORNECEDOR: "Sugerida pelo código do fornecedor",
  CODIGO_INTERNO: "Sugerida pelo código interno",
};

function initialRows(preview) {
  const previous = new Map((preview.plano_anterior || []).map((p) => [p.n, p]));
  const rows = {};
  for (const item of preview.nfe.itens) {
    const prior = previous.get(item.n);
    rows[item.n] = prior
      ? { part_id: prior.part_id, quantidade: String(prior.quantidade) }
      : { part_id: item.sugestao?.id || "", quantidade: item.quantidade_inteira != null ? String(item.quantidade_inteira) : "" };
  }
  return rows;
}

export default function NfeEntry() {
  const fileInput = useRef(null);
  const [parts, setParts] = useState([]);
  const [partsLoaded, setPartsLoaded] = useState(false);
  const [xml, setXml] = useState("");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/parts")
      .then((r) => { setParts(Array.isArray(r.data) ? r.data : r.data.items || []); setPartsLoaded(true); })
      .catch(() => {});
  }, []);

  const sortedParts = useMemo(
    () => [...parts].sort((a, b) => String(a.descricao).localeCompare(String(b.descricao), "pt-BR")),
    [parts],
  );

  const done = preview?.importacao?.status === "CONCLUIDA";
  const resuming = Boolean(preview?.plano_anterior);
  const locked = done || resuming || loading;
  const noParts = partsLoaded && parts.length === 0;
  const linked = preview ? preview.nfe.itens.filter((i) => rows[i.n]?.part_id) : [];

  async function onFile(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error("Arquivo maior que 2 MB."); return; }
    setLoading(true); setResult(null); setPreview(null);
    try {
      const text = await file.text();
      const r = await api.post("/movements/nfe/preview", { xml: text });
      setXml(text); setFileName(file.name); setPreview(r.data); setRows(initialRows(r.data));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }

  function update(n, patch) {
    setRows((current) => ({ ...current, [n]: { ...current[n], ...patch } }));
  }

  async function confirm() {
    const itens = preview.nfe.itens.map((i) => ({
      n: i.n,
      part_id: rows[i.n]?.part_id || null,
      quantidade: rows[i.n]?.part_id ? Number(rows[i.n].quantidade) : null,
    }));
    const invalid = itens.find((i) => i.part_id && !(Number.isInteger(i.quantidade) && i.quantidade > 0));
    if (invalid) { toast.error(`Item ${invalid.n}: informe uma quantidade inteira maior que zero.`); return; }
    if (!itens.some((i) => i.part_id)) { toast.error("Vincule ao menos um item a uma peça."); return; }
    setLoading(true);
    try {
      const r = await api.post("/movements/nfe/confirm", { xml, itens });
      setResult(r.data);
      setPreview(null); setXml(""); setFileName("");
      toast.success(`Entrada lançada: ${r.data.itens_lancados} item(ns) da NF-e ${r.data.numero}.`);
    } catch (e) {
      // O lançamento é idempotente pela chave da nota: repetir é seguro.
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }

  // Usado na tabela (computador) e nos cartões (celular); `suffix` mantém ids únicos.
  function partSelect(item, suffix) {
    const row = rows[item.n] || {};
    const id = `nfe-part-${item.n}${suffix}`;
    return (
      <div>
        <label className={suffix ? "block text-xs text-slate-400 mb-1" : "sr-only"} htmlFor={id}>
          {suffix ? "Peça no estoque" : `Peça para o item ${item.n}`}
        </label>
        <select
          id={id}
          data-testid={id}
          disabled={locked}
          value={row.part_id || ""}
          onChange={(e) => update(item.n, { part_id: e.target.value })}
          className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2.5 text-slate-100"
        >
          <option value="">— Não lançar este item —</option>
          {sortedParts.map((p) => (
            <option key={p.id} value={p.id}>{p.descricao} ({p.codigo_interno})</option>
          ))}
        </select>
        {item.sugestao && row.part_id === item.sugestao.id && (
          <div className="text-xs text-emerald-400 mt-1">{ORIGEM[item.sugestao.origem] || "Sugerida pelo cadastro"}</div>
        )}
        {!row.part_id && !done && !noParts && <div className="text-xs text-slate-500 mt-1">Escolha a peça ou deixe sem lançar.</div>}
      </div>
    );
  }

  function quantityInput(item, suffix) {
    const row = rows[item.n] || {};
    const id = `nfe-qtd-${item.n}${suffix}`;
    return (
      <div className={suffix ? "" : "text-right"}>
        <label className={suffix ? "block text-xs text-slate-400 mb-1" : "sr-only"} htmlFor={id}>
          {suffix ? "Qtd a lançar" : `Quantidade a lançar do item ${item.n}`}
        </label>
        <input
          id={id}
          data-testid={id}
          type="number" min="1" step="1" inputMode="numeric"
          disabled={!row.part_id || locked}
          value={row.quantidade ?? ""}
          onChange={(e) => update(item.n, { quantidade: e.target.value })}
          className={`${suffix ? "w-full" : "w-24"} rounded-md border border-slate-700 bg-slate-900 px-3 py-2.5 text-right font-mono text-slate-100 disabled:opacity-40`}
        />
        {item.quantidade_inteira == null && row.part_id && (
          <div className={`text-xs text-amber-300 mt-1 ${suffix ? "" : "max-w-[180px] ml-auto"}`}>Quantidade fracionada: informe quantas unidades de estoque isso representa.</div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-50">Entrada por NF-e</h1>
          <p className="text-slate-400 text-sm">Envie o XML da nota do fornecedor, confira os itens e lance a entrada no estoque.</p>
        </div>
        <Button data-testid="nfe-pick-file" className="bg-sky-700 hover:bg-sky-600" disabled={loading} onClick={() => fileInput.current?.click()}>
          <FileUp className="h-4 w-4 mr-2" />{preview ? "Trocar arquivo" : "Selecionar XML"}
        </Button>
        <input ref={fileInput} type="file" accept=".xml,text/xml,application/xml" className="hidden" onChange={onFile} data-testid="nfe-file-input" />
      </div>

      {noParts && (
        <Card className="p-4 border-sky-700/60 bg-sky-950/30 text-sky-100 flex flex-wrap items-center gap-3" data-testid="nfe-no-parts">
          <PackagePlus className="h-5 w-5 shrink-0 text-sky-300" />
          <div className="flex-1 min-w-[220px]">Nenhuma peça cadastrada nesta empresa. Cadastre as peças antes de lançar a nota, para poder vincular cada item.</div>
          <Button asChild variant="secondary"><Link to="/pecas">Cadastrar peças</Link></Button>
        </Card>
      )}

      {result && (
        <Card className="card-metal p-5 flex flex-wrap items-center gap-3" data-testid="nfe-result">
          <CheckCircle2 className="h-6 w-6 text-emerald-400" />
          <div className="flex-1 min-w-[220px] text-slate-200">
            NF-e {result.numero}: {result.itens_lancados} item(ns) lançado(s) no estoque.
            {result.retomado && <span className="text-slate-400"> Lançamento retomado de uma tentativa anterior.</span>}
          </div>
          <Button asChild variant="secondary"><Link to="/movimentacoes">Ver movimentações</Link></Button>
        </Card>
      )}

      {!preview && !result && (
        <Card className="card-metal p-10 text-center text-slate-400">
          <Receipt className="h-10 w-10 mx-auto mb-3 opacity-60" />
          {loading ? "Lendo a nota..." : "Selecione o arquivo XML da NF-e (o mesmo que o fornecedor envia por e-mail). O PDF/DANFE não serve."}
        </Card>
      )}

      {preview && (
        <>
          <Card className="card-metal p-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 text-sm">
            <div><div className="text-slate-500 text-xs uppercase font-mono">Nota</div><div className="text-slate-100">{preview.nfe.numero}/{preview.nfe.serie} · {fmtDate(preview.nfe.data_emissao)}</div></div>
            <div>
              <div className="text-slate-500 text-xs uppercase font-mono">Fornecedor</div>
              <div className="text-slate-100">{preview.nfe.emitente.fantasia || preview.nfe.emitente.nome}</div>
              <div className="text-slate-500 text-xs">{preview.fornecedor ? `Cadastrado: ${preview.fornecedor.nome}` : `CNPJ ${preview.nfe.emitente.cnpj} não cadastrado em Fornecedores`}</div>
            </div>
            <div><div className="text-slate-500 text-xs uppercase font-mono">Valor da nota</div><div className="text-slate-100">{brl(preview.nfe.valor_total)}</div></div>
            <div><div className="text-slate-500 text-xs uppercase font-mono">Arquivo</div><div className="text-slate-300 break-all">{fileName}</div></div>
          </Card>

          {done && (
            <Card className="p-4 border-amber-700/60 bg-amber-950/30 text-amber-200 flex gap-3" data-testid="nfe-already">
              <AlertTriangle className="h-5 w-5 shrink-0" />Esta NF-e já foi lançada no estoque em {fmtDate(preview.importacao.concluido_em)}{preview.importacao.criado_por_nome ? ` por ${preview.importacao.criado_por_nome}` : ""}.
            </Card>
          )}
          {resuming && !done && (
            <Card className="p-4 border-sky-700/60 bg-sky-950/30 text-sky-200 flex gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0" />Há um lançamento desta nota iniciado e não concluído. Ao confirmar, ele será terminado com os itens escolhidos na primeira vez, sem duplicar o estoque.
            </Card>
          )}
          {preview.destinatario_confere === false && (
            <Card className="p-4 border-amber-700/60 bg-amber-950/30 text-amber-200 flex gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0" />O CNPJ do destinatário da nota é diferente do CNPJ da sua empresa. Confira se a nota é mesmo sua.
            </Card>
          )}

          {/* Computador e tablet: tabela */}
          <Card className="card-metal p-0 overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-4 py-3">Produto na nota</th>
                  <th className="text-right px-4 py-3">Qtd na nota</th>
                  <th className="text-left px-4 py-3 min-w-[260px]">Peça no estoque</th>
                  <th className="text-right px-4 py-3">Qtd a lançar</th>
                </tr></thead>
                <tbody>
                  {preview.nfe.itens.map((item) => (
                    <tr key={item.n} className="border-t border-slate-800 align-top" data-testid={`nfe-item-${item.n}`}>
                      <td className="px-4 py-3 text-slate-500 font-mono">{item.n}</td>
                      <td className="px-4 py-3">
                        <div className="text-slate-200">{item.descricao}</div>
                        <div className="text-slate-500 text-xs font-mono">{item.codigo}{item.ean ? ` · EAN ${item.ean}` : ""} · {brl(item.valor_unitario)}/{item.unidade}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-300 whitespace-nowrap">{item.quantidade.toLocaleString("pt-BR")} {item.unidade}</td>
                      <td className="px-4 py-3">{partSelect(item, "")}</td>
                      <td className="px-4 py-3">{quantityInput(item, "")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Celular: um cartão por item, sem rolagem lateral */}
          <div className="space-y-3 md:hidden">
            {preview.nfe.itens.map((item) => (
              <Card key={item.n} className="card-metal p-4 space-y-3" data-testid={`nfe-card-${item.n}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-slate-500 text-xs font-mono">Item {item.n} · {item.codigo}</div>
                    <div className="text-slate-100 font-medium">{item.descricao}</div>
                    <div className="text-slate-500 text-xs">{brl(item.valor_unitario)}/{item.unidade}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-slate-500 text-xs">Na nota</div>
                    <div className="font-mono text-slate-200 whitespace-nowrap">{item.quantidade.toLocaleString("pt-BR")} {item.unidade}</div>
                  </div>
                </div>
                {partSelect(item, "-m")}
                {quantityInput(item, "-m")}
              </Card>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <span className="text-sm text-slate-400 w-full sm:w-auto text-right">{linked.length} de {preview.nfe.itens.length} item(ns) serão lançados</span>
            <Button variant="secondary" disabled={loading} onClick={() => { setPreview(null); setXml(""); setFileName(""); }}>Cancelar</Button>
            <Button data-testid="nfe-confirm" className="bg-emerald-600 hover:bg-emerald-500" disabled={done || loading || linked.length === 0} onClick={confirm}>
              {loading ? "Lançando..." : resuming ? "Concluir lançamento" : "Confirmar entrada"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
